import { AttributeState } from "./types";
import { Attribute, AttributeOption } from "ProductGrid/Attribution/types";
import { CaseReducer, PayloadAction } from "@reduxjs/toolkit";

enum AttributeActionType {
  SELECT_OPTION = "selectOption",
  DESELECT_OPTION = "deselectOption",
  RESET_STATE = "resetState",
}

type ResetStateActionPayload = {
  attributes: Attribute[];
};
const resetState: CaseReducer<
  AttributeState,
  PayloadAction<ResetStateActionPayload>
> = (_, action) => {
  const { attributes } = action.payload;
  return {
    attributes,
    selectedOptions: attributes.reduce(
      (acc, attribute) => ({
        ...acc,
        [attribute.id]: null,
      }),
      {}
    ),
  };
};

type SelectOptionActionPayload = {
  option: AttributeOption;
  attribute: Attribute;
};
const selectOption: CaseReducer<
  AttributeState,
  PayloadAction<SelectOptionActionPayload>
> = (prevState, action) => {
  const { option, attribute } = action.payload;
  const { id: attributeId, isMultiSelect } = attribute;
  const prevSelectionsForAttribute =
    prevState.selectedOptions[attributeId] ?? [];
  return {
    ...prevState,
    selectedOptions: {
      ...prevState.selectedOptions,
      [attributeId]: isMultiSelect
        ? [...prevSelectionsForAttribute, option]
        : [option],
    },
  };
};

type DeselectOptionActionPayload = {
  option: AttributeOption;
  attribute: Attribute;
};

const getChildrenForClearing = (attribute: Attribute) => {
  const getAllChildrenIds = (
    attributeToSearch: Attribute,
    foundChildren: string[] = []
  ): string[] => {
    if (attributeToSearch.children.length < 1) {
      return foundChildren;
    }
    return attributeToSearch.children.reduce((acc, child) => {
      return [...acc, child.id, ...getAllChildrenIds(child, acc)];
    }, [] as string[]);
  };
  return getAllChildrenIds(attribute).reduce((acc, childId) => {
    return {
      ...acc,
      [childId]: null,
    };
  }, {});
};
const deselectOption: CaseReducer<
  AttributeState,
  PayloadAction<DeselectOptionActionPayload>
> = (prevState, action): AttributeState => {
  const { option, attribute } = action.payload;
  const { id: attributeId, isMultiSelect } = attribute;
  const prevSelectionsForAttribute =
    prevState.selectedOptions[attributeId] ?? [];
  const newSelections = isMultiSelect
    ? prevSelectionsForAttribute.filter(
        (selectedOption) => selectedOption.id !== option.id
      )
    : null;
  const clearedChildren =
    newSelections === null || newSelections.length < 1
      ? getChildrenForClearing(attribute)
      : {};
  return {
    ...prevState,
    selectedOptions: {
      ...prevState.selectedOptions,
      [attributeId]: newSelections,
      ...clearedChildren,
    },
  };
};

const reducers: Record<
  AttributeActionType,
  CaseReducer<AttributeState, any>
> = {
  [AttributeActionType.RESET_STATE]: resetState,
  [AttributeActionType.SELECT_OPTION]: selectOption,
  [AttributeActionType.DESELECT_OPTION]: deselectOption,
};

export default reducers;
